import { useState } from "react";

import "./styles/font.css";
import "./styles/theme.css";
import "./App.css";

// Set to false to put the maintenance notice up in place of everything below
// the headline. It replaced a `down` flag that read the other way round, which
// meant the page shipped saying `let down = true` while it was working fine.
const LIVE = true;

const PORTALS = [
  {
    // Straight to the admin, not to auction.spectors.in/admin. That path is a
    // React route whose whole job is to redirect here, so going through it
    // costs a page load and a flash of "Loading" for no reason. The old path
    // still works for anyone who has it bookmarked.
    name: "Admin portal",
    href: "https://auctionadmin.spectors.in",
    url: "auctionadmin.spectors.in",
    icon: "sliders",
  },
  {
    name: "Curator portal",
    href: "https://auction.spectors.in/curator",
    url: "auction.spectors.in/curator",
    icon: "broadcast",
  },
  {
    name: "Bidder portal",
    href: "https://auction.spectors.in/bidder",
    url: "auction.spectors.in/bidder",
    icon: "users",
  },
];

// Drawn inline on a 24x24 box, stroked in `currentColor`, so they take their
// colour from whatever contains them and need no per-icon override.
function Icon({ name }) {
  const common = {
    width: 20,
    height: 20,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.7,
    strokeLinecap: "round",
    strokeLinejoin: "round",
  };

  if (name === "sliders") {
    return (
      <svg {...common}>
        <path d="M4 7h9M19 7h1M4 17h3M13 17h7" />
        <circle cx="16" cy="7" r="2.3" />
        <circle cx="10" cy="17" r="2.3" />
      </svg>
    );
  }

  if (name === "broadcast") {
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="2.2" />
        <path d="M7.8 7.8a6 6 0 0 0 0 8.4M16.2 16.2a6 6 0 0 0 0-8.4" />
        <path d="M4.9 4.9a10 10 0 0 0 0 14.2M19.1 19.1a10 10 0 0 0 0-14.2" />
      </svg>
    );
  }

  if (name === "users") {
    return (
      <svg {...common}>
        <path d="M15.5 19.5v-1.7a3.4 3.4 0 0 0-3.4-3.4H6.4A3.4 3.4 0 0 0 3 17.8v1.7" />
        <circle cx="9.2" cy="7.6" r="3.3" />
        <path d="M21 19.5v-1.7a3.4 3.4 0 0 0-2.6-3.3M15.4 4.8a3.4 3.4 0 0 1 0 6.5" />
      </svg>
    );
  }

  return null;
}

// The gavel, kept from the old page but drawn rather than loaded: the PNG it
// replaces was 32px wide and went to mush at button size. Three strokes at
// three weights, because the head of a gavel is much thicker than its handle.
function Gavel() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M14.2 3.8 20.2 9.8" strokeWidth="4.4" />
      <path d="M15.4 8.6 8.9 15.1" strokeWidth="1.9" />
      <path d="M3.6 19.8h9.2" strokeWidth="2.4" />
    </svg>
  );
}

function App() {
  const [sessionID, setSessionID] = useState("");
  const [error, setError] = useState("");

  // Unchanged from the old page: a session ID is longer than ten characters.
  // What changed is that a bad one now says so under the field instead of
  // throwing a browser alert.
  const openLiveScreen = (event) => {
    event.preventDefault();
    const id = sessionID.trim();

    if (id.length <= 10) {
      setError("That does not look like a session ID. Check it with your curator.");
      return;
    }

    setError("");
    window.location.href = `https://auction.spectors.in/live/${id}`;
  };

  return (
    <div className="page">
      <div className="wrap">
        <header className="hero rise">
          <span className="eyebrow">Free to use</span>
          <h1>Mock Auction Software</h1>
          <p className="lede">
            Run an IPL-style player auction with your own teams, owners and
            player pool. Bidders join from their phones, and the room follows it
            on the live screen.
          </p>

          {LIVE && (
            <div className="hero-cta">
              <a
                className="btn primary big"
                href="https://auctionadmin.spectors.in/signup"
              >
                Get it free
                <Gavel />
              </a>
            </div>
          )}
        </header>

        {!LIVE ? (
          <section className="card notice rise">
            <h2>Down for maintenance</h2>
            <p className="sub">
              The software is being updated. Please check back shortly.
            </p>
          </section>
        ) : (
          <>
            <section className="card rise">
              <h2>How to play</h2>
              <p className="sub">
                A full walkthrough, from setting up your teams to selling the
                last player.
              </p>
              <div className="video">
                <iframe
                  src="https://www.youtube.com/embed/nOeJQv-H5zI"
                  title="How to use SPECAUC Mock auction software - Full Tutorial"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                />
              </div>
            </section>

            <section className="card rise">
              <h2>Live screen</h2>
              <p className="sub">
                Enter the session ID your curator gives you to follow the
                auction as it happens.
              </p>

              <form className="join-form" onSubmit={openLiveScreen}>
                <div className="field">
                  <label className="label" htmlFor="session">
                    Session ID
                  </label>
                  <input
                    id="session"
                    className="input"
                    value={sessionID}
                    placeholder="e.g. wwe-xi3-n58"
                    onChange={(e) => {
                      setSessionID(e.target.value);
                      if (error) setError("");
                    }}
                  />
                </div>
                <button type="submit" className="btn primary">
                  Go
                  <Gavel />
                </button>
              </form>

              {error && <p className="error">{error}</p>}
            </section>

            <div>
              <span className="label">Portals</span>
              <div className="portals">
                {PORTALS.map((p) => (
                  <a key={p.href} className="portal rise" href={p.href}>
                    <span className="portal-icon">
                      <Icon name={p.icon} />
                    </span>
                    <span className="portal-text">
                      <span className="portal-name">{p.name}</span>
                      <span className="portal-url">{p.url}</span>
                    </span>
                  </a>
                ))}
              </div>
            </div>
          </>
        )}

        <footer className="foot">
          For any queries, contact{" "}
          <a href="mailto:specauc@gmail.com">specauc@gmail.com</a>
        </footer>
      </div>
    </div>
  );
}

export default App;
